import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Command as CommandPrimitive } from 'cmdk';
import {
  CommandEmpty, CommandGroup, CommandInput,
  CommandItem, CommandList, CommandSeparator,
} from './ui/command';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import {
  BookOpen, FileText, Truck, User, Building, Wrench, Folder,
  Search as SearchIcon, Loader2, LayoutDashboard, Plus, RefreshCw,
  Flag, ShoppingCart, DollarSign, Users, Settings, Zap, Calendar, Clock, X,
  Filter, CheckCircle, XCircle, ArrowRight, ArrowLeft, Trash2, Eye, Send, Download, Receipt,
  CalendarClock, Sparkles, Star, ShieldCheck, ClipboardList, TrendingUp, Package, Inbox,
} from 'lucide-react';
import api from '../lib/api';
import { toast } from '../hooks/use-toast';
import { getRecents, trackRecent, clearRecents } from '../lib/recents';
import { useCmdK } from '../contexts/CmdKContext';

const ICONS = {
  BookOpen, FileText, Truck, User, Building, Wrench, Folder,
  LayoutDashboard, Plus, RefreshCw, Flag, ShoppingCart, DollarSign,
  Users, Settings, Zap, Calendar, Receipt,
  Filter, CheckCircle, XCircle, ArrowRight, ArrowLeft, Trash2, Eye, Send, X,
  CalendarClock, Sparkles, Star, ShieldCheck, ClipboardList, TrendingUp, Package, Inbox,
};

const TYPE_LABELS = {
  wiki: 'Manual',
  oc: 'Órdenes de Compra',
  dte: 'DTEs · Facturas SII',
  proveedor: 'Proveedores',
  trabajador: 'Trabajadores',
  cliente: 'Clientes',
  ot: 'Órdenes de Trabajo',
  proyecto: 'Proyectos',
};

const TYPE_ORDER = ['oc', 'dte', 'ot', 'proyecto', 'proveedor', 'cliente', 'trabajador', 'wiki'];

// ═══════════════════════════════════════════════════════════════════════
//  Comandos de acción (cliente-side, siempre disponibles)
// ═══════════════════════════════════════════════════════════════════════

const ACTION_COMMANDS = [
  // Navegación rápida
  { id: 'go-dashboard', kind: 'nav', icon: 'LayoutDashboard', label: 'Ir a Dashboard', sublabel: 'Vista general', keywords: 'inicio home dashboard', route: '/dashboard' },
  { id: 'go-manual', kind: 'nav', icon: 'BookOpen', label: 'Ir al Manual de Uso', sublabel: 'Wiki interno', keywords: 'wiki manual ayuda help', route: '/manual' },
  { id: 'go-flujo', kind: 'nav', icon: 'DollarSign', label: 'Ir a Flujo de Caja', sublabel: 'Tesorería · CxC · Factoring', keywords: 'flujo caja tesoreria cxc factoring', route: '/contabilidad/flujo-caja' },
  { id: 'go-cxp-nominas', kind: 'nav', icon: 'DollarSign', label: 'Ir a CxP Nóminas (Viernes)', sublabel: 'Cuentas por pagar agrupadas por viernes', keywords: 'cxp cuentas pagar nominas viernes', route: '/contabilidad/cxp-nominas' },
  { id: 'go-cxc', kind: 'nav', icon: 'TrendingUp', label: 'Ir a Cuentas por Cobrar', sublabel: 'CxC · Cobranzas', keywords: 'cxc cuentas cobrar cobranzas', route: '/cuentas-por-cobrar' },
  { id: 'go-ocs', kind: 'nav', icon: 'ShoppingCart', label: 'Ir a Listado OC', sublabel: 'Órdenes de Compra', keywords: 'oc ordenes compra listado', route: '/compras/ordenes-compra' },
  { id: 'go-requis', kind: 'nav', icon: 'ClipboardList', label: 'Ir a Requisiciones', sublabel: 'Pedidos pendientes a OC', keywords: 'requisiciones pedidos solicitudes', route: '/compras/requisiciones' },
  { id: 'go-prov', kind: 'nav', icon: 'Truck', label: 'Ir a Proveedores', sublabel: 'Gestión proveedores', keywords: 'proveedores supplier vendor', route: '/compras/proveedores' },
  { id: 'go-cotizador', kind: 'nav', icon: 'DollarSign', label: 'Ir a Cotizador Histórico', sublabel: 'Precios desde compras pasadas', keywords: 'cotizador precios historico cotizacion', route: '/informacion/cotizador-historico' },
  { id: 'go-dte', kind: 'nav', icon: 'FileText', label: 'Ir a Acepta/Rechazo DTE', sublabel: 'Facturas SII', keywords: 'dte sii facturas acepta rechazo', route: '/contabilidad/acepta-rechazo-dte' },
  { id: 'go-reportes-prog', kind: 'nav', icon: 'CalendarClock', label: 'Ir a Reportes Programados', sublabel: 'Envío automático por correo', keywords: 'reportes programados automatico email envio', route: '/configuracion/reportes-programados', adminOnly: true },
  { id: 'go-asist', kind: 'nav', icon: 'Calendar', label: 'Ir a Asistencia Mensual', sublabel: 'RRHH · Asistencia', keywords: 'asistencia rrhh mes mensual feriados', route: '/rrhh/asistencia-mensual' },
  { id: 'go-mis-vb', kind: 'nav', icon: 'ShieldCheck', label: 'Ir a Mis VB Supervisor', sublabel: 'Partes pendientes de aprobar', keywords: 'vb visto bueno supervisor aprobar', route: '/rrhh/mis-vb' },
  { id: 'go-mis-partes', kind: 'nav', icon: 'ClipboardList', label: 'Ir a Mis Partes', sublabel: 'Partes que creé este mes', keywords: 'mis partes diarios creados mes', route: '/rrhh/mis-partes' },
  { id: 'go-trab', kind: 'nav', icon: 'Users', label: 'Ir a Trabajadores', sublabel: 'Personal · Fichas', keywords: 'trabajadores personal staff', route: '/rrhh/trabajadores' },
  { id: 'go-stock', kind: 'nav', icon: 'Package', label: 'Ir a Stock Insumos y EPP', sublabel: 'Bodega · Stock crítico', keywords: 'stock insumos epp bodega', route: '/bodega/stock-insumos' },
  { id: 'go-bandeja', kind: 'nav', icon: 'Inbox', label: 'Ir a Bandeja de Aprobaciones', sublabel: 'Pendientes asignados a ti', keywords: 'bandeja aprobaciones pendientes inbox', route: '/aprobaciones' },
  { id: 'go-config', kind: 'nav', icon: 'Settings', label: 'Ir a Configuración', sublabel: 'Empresa · Email · Tarjetas', keywords: 'config configuracion ajustes empresa', route: '/configuracion' },
  // Crear nuevo
  { id: 'new-oc', kind: 'create', icon: 'Plus', label: 'Crear nueva OC', sublabel: 'Generar Orden de Compra', keywords: 'crear nueva oc orden compra generar', route: '/compras/generar-oc' },
  { id: 'new-prov', kind: 'create', icon: 'Plus', label: 'Crear nuevo Proveedor', sublabel: 'Manual', keywords: 'crear nuevo proveedor manual', route: '/compras/proveedores?action=new' },
  { id: 'new-articulo', kind: 'create', icon: 'Plus', label: 'Crear artículo del Manual', sublabel: 'Wiki interno', keywords: 'crear articulo manual wiki', route: '/manual?action=new', adminOnly: true },
  { id: 'new-prof', kind: 'create', icon: 'Plus', label: 'Crear nueva Proforma', sublabel: 'Venta', keywords: 'crear proforma factura venta', route: '/contabilidad/proforma-factura' },
  { id: 'new-reporte-prog', kind: 'create', icon: 'Plus', label: 'Crear nuevo Reporte Programado', sublabel: 'Excel por correo automático', keywords: 'crear reporte programado automatico email', route: '/configuracion/reportes-programados?action=new', adminOnly: true },
  { id: 'new-requis', kind: 'create', icon: 'Plus', label: 'Crear nueva Requisición', sublabel: 'Pedido a Compras', keywords: 'crear requisicion pedido', route: '/compras/requisiciones?action=new' },
  // Acciones (api calls)
  { id: 'sync-all', kind: 'action', icon: 'Zap', label: 'Sincronizar TODO ahora', sublabel: 'Alegra · DTE · Contabilización · CxC', keywords: 'sync sincronizar alegra todo all', endpoint: 'sync-all' },
  { id: 'sync-alegra', kind: 'action', icon: 'RefreshCw', label: 'Sincronizar Alegra (CxC)', sublabel: 'Refresh cuentas por cobrar', keywords: 'sync alegra cxc cuentas cobrar', endpoint: 'sync-alegra' },
  { id: 'feriados', kind: 'action', icon: 'Flag', label: 'Marcar feriados del mes actual', sublabel: 'api.boostr.cl', keywords: 'feriados festivos boostr asistencia', endpoint: 'feriados' },
  // Comandos del tour / ayuda
  { id: 'show-tutorial', kind: 'action', icon: 'Sparkles', label: 'Ver tutorial de bienvenida', sublabel: 'Mini-tour 3 pasos', keywords: 'tutorial onboarding tour ayuda', event: 'chirino-show-onboarding' },
  { id: 'show-shortcuts', kind: 'action', icon: 'Sparkles', label: 'Ver atajos de teclado', sublabel: 'Lista completa (g+f, c+o, ...)', keywords: 'atajos teclado shortcuts keyboard help ayuda', kbEvent: '?' },
  { id: 'reset-favs', kind: 'action', icon: 'Star', label: 'Limpiar mis favoritos del sidebar', sublabel: 'Borra todas las estrellas', keywords: 'favoritos estrellas limpiar reset', event: 'chirino-clear-favoritos' },
];

export function GlobalSearch({ showTrigger = true }) {
  const navigate = useNavigate();
  const { contextCommands } = useCmdK();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recents, setRecents] = useState([]);
  const debRef = useRef(null);

  // Cargar recientes cuando se abre
  useEffect(() => {
    if (!open) return;
    setRecents(getRecents());
  }, [open]);

  // Atajo de teclado Cmd/Ctrl + K
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Debounced search
  useEffect(() => {
    if (debRef.current) clearTimeout(debRef.current);
    if (!q.trim()) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setResults([]);  // limpia stale results antes de la nueva consulta
    debRef.current = setTimeout(async () => {
      try {
        const res = await api.get('/search/global', { params: { q } });
        setResults(res.data.results || []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => debRef.current && clearTimeout(debRef.current);
  }, [q]);

  // Reset al abrir/cerrar
  useEffect(() => {
    if (!open) { setQ(''); setResults([]); }
  }, [open]);

  const grouped = results.reduce((acc, r) => {
    if (!acc[r.type]) acc[r.type] = [];
    acc[r.type].push(r);
    return acc;
  }, {});

  // Filtrar comandos según query (case-insensitive en label/sublabel/keywords)
  const filteredCommands = useMemo(() => {
    if (!q.trim()) return ACTION_COMMANDS;
    const qLower = q.toLowerCase();
    return ACTION_COMMANDS.filter((c) => {
      const haystack = `${c.label} ${c.sublabel} ${c.keywords || ''}`.toLowerCase();
      return haystack.includes(qLower);
    });
  }, [q]);

  const commandsByKind = useMemo(() => {
    const out = { nav: [], create: [], action: [] };
    for (const c of filteredCommands) {
      if (out[c.kind]) out[c.kind].push(c);
    }
    return out;
  }, [filteredCommands]);

  // Comandos contextuales (registrados por la página activa vía CmdKContext).
  // Se filtran también por query — si matchea label/sublabel/keywords o nsLabel.
  const filteredContextCommands = useMemo(() => {
    if (!q.trim()) return contextCommands;
    const qLower = q.toLowerCase();
    return contextCommands.filter((c) => {
      const haystack = `${c.label || ''} ${c.sublabel || ''} ${c.keywords || ''} ${c._nsLabel || ''}`.toLowerCase();
      return haystack.includes(qLower);
    });
  }, [q, contextCommands]);

  const executeAction = useCallback(async (cmd) => {
    setOpen(false);
    if (cmd.kind === 'nav' || cmd.kind === 'create') {
      if (cmd.route) navigate(cmd.route);
      return;
    }
    if (cmd.kind !== 'action') return;
    try {
      if (cmd.endpoint === 'sync-all') {
        toast({ title: 'Sincronización masiva iniciada', description: 'Esto puede tardar 1-2 minutos...' });
        const res = await api.post('/sync-center/run-all');
        const summary = (res.data?.results || []).map((r) => `${r.label}: ${r.status}`).join(' · ');
        toast({ title: 'Sync completo', description: summary });
      } else if (cmd.endpoint === 'sync-alegra') {
        toast({ title: 'Refrescando CxC desde Alegra...' });
        await api.post('/sync-center/run/alegra_cxc');
        toast({ title: 'CxC sincronizado', description: 'Flujo de Caja actualizado' });
      } else if (cmd.endpoint === 'feriados') {
        const now = new Date();
        const mes = `${String(now.getMonth() + 1).padStart(2, '0')}/${String(now.getFullYear() % 100).padStart(2, '0')}`;
        toast({ title: `Marcando feriados de ${mes}...` });
        const res = await api.post('/rrhh/asistencia-mensual/feriados/auto-marcar', { mes });
        toast({
          title: `${res.data.marcados} feriados marcados`,
          description: (res.data.feriados_aplicados || []).map((f) => `D${f.dia}: ${f.nombre}`).join(' · ') || 'Sin feriados oficiales',
        });
      } else if (cmd.event) {
        // Comandos que disparan un CustomEvent global (ej. mostrar onboarding, limpiar favoritos)
        window.dispatchEvent(new CustomEvent(cmd.event));
        if (cmd.id === 'reset-favs') {
          toast({ title: 'Favoritos limpiados', description: 'Todas tus estrellas del sidebar fueron removidas.' });
        }
      } else if (cmd.kbEvent) {
        // Comandos que abren paneles globales (ej. "?" → modal de atajos).
        // En lugar de simular pulsación (que se pierde por focus en cmdk input),
        // usamos un callback expuesto vía window.__openShortcutsHelp por
        // GlobalShortcutsLayer. Si no está disponible, fallback a KeyboardEvent.
        if (cmd.kbEvent === '?' && typeof window.__openShortcutsHelp === 'function') {
          // Pequeño delay para que cmdk termine de desmontarse y no robe focus.
          setTimeout(() => window.__openShortcutsHelp(true), 60);
        } else {
          setTimeout(() => window.dispatchEvent(new KeyboardEvent('keydown', { key: cmd.kbEvent })), 60);
        }
      }
    } catch (err) {
      toast({ title: 'Error en acción', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    }
  }, [navigate]);

  const handleSelect = useCallback((result) => {
    setOpen(false);
    if (!result) return;
    // Trackear como reciente (solo entidades reales del servidor, no comandos)
    if (result.type && result.label) {
      trackRecent({
        type: result.type,
        id: result.id || result.label,
        label: result.label,
        sublabel: result.sublabel,
        route: result.route,
        icon: result.icon,
      });
    }
    if (result.route) navigate(result.route);
  }, [navigate]);

  const handleClearRecents = useCallback(() => {
    clearRecents();
    setRecents([]);
  }, []);

  const executeContextCommand = useCallback(async (cmd) => {
    setOpen(false);
    try {
      if (typeof cmd.run === 'function') {
        await cmd.run();
      } else if (cmd.route) {
        navigate(cmd.route);
      }
    } catch (err) {
      toast({
        title: 'Error al ejecutar comando',
        description: err?.response?.data?.detail || err?.message || 'Intenta nuevamente',
        variant: 'destructive',
      });
    }
  }, [navigate]);

  return (
    <>
      {/* Trigger visible (botón en topbar/sidebar) */}
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 h-8 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-xs text-slate-600 dark:text-slate-300 transition-colors w-full justify-start"
        data-testid="global-search-trigger"
      >
        <SearchIcon className="w-3.5 h-3.5 flex-shrink-0" />
        <span className="flex-1 text-left">Buscar...</span>
        <kbd className="hidden md:inline-flex pointer-events-none h-5 select-none items-center gap-0.5 rounded border bg-white dark:bg-slate-900 px-1 font-mono text-[10px] text-slate-500">
          <span className="text-xs">⌘</span>K
        </kbd>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="overflow-hidden p-0 max-w-2xl" data-testid="global-search-dialog">
          <DialogTitle className="sr-only">Búsqueda global y comandos</DialogTitle>
          <CommandPrimitive
            shouldFilter={false}
            className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5 flex h-full w-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground"
          >
            <CommandInput
              placeholder="Buscar OC, factura/DTE, proveedor, trabajador, cliente, OT, manual..."
              value={q}
              onValueChange={setQ}
              data-testid="global-search-input"
            />
            <CommandList>
              {loading && (
                <div className="flex items-center justify-center py-3 text-xs text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Buscando...
                </div>
              )}

              {/* Comandos contextuales — registrados por la página activa.
                  Siempre al tope, destacados con acento esmeralda. */}
              {filteredContextCommands.length > 0 && (
                <>
                  <CommandGroup heading={
                    <span className="flex items-center gap-1 text-emerald-700 dark:text-emerald-400">
                      <Zap className="w-3 h-3" />
                      En esta página
                      {filteredContextCommands[0]?._nsLabel && (
                        <span className="text-[10px] text-muted-foreground">· {filteredContextCommands[0]._nsLabel}</span>
                      )}
                    </span>
                  }>
                    {filteredContextCommands.map((cmd) => {
                      const Icon = ICONS[cmd.icon] || Zap;
                      return (
                        <CommandItem
                          key={`ctx-${cmd._ns}-${cmd.id}`}
                          value={`ctx-${cmd._ns}-${cmd.id}-${cmd.label}`}
                          onSelect={() => executeContextCommand(cmd)}
                          data-testid={`ctx-cmd-${cmd.id}`}
                          className="flex items-start gap-2 cursor-pointer border-l-2 border-l-emerald-500/40"
                        >
                          <Icon className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-600" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{cmd.label}</div>
                            {cmd.sublabel && (
                              <div className="text-[11px] text-muted-foreground truncate">{cmd.sublabel}</div>
                            )}
                          </div>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                  <CommandSeparator />
                </>
              )}

              {/* Recientes (solo cuando query vacío) */}
              {!q.trim() && recents.length > 0 && (
                <>
                  <CommandGroup
                    heading={
                      <div className="flex items-center justify-between w-full pr-2">
                        <span>Recientes</span>
                        <button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleClearRecents(); }}
                          className="text-[10px] text-muted-foreground hover:text-red-600 transition-colors"
                          data-testid="btn-clear-recents"
                          title="Limpiar historial reciente"
                        >
                          <X className="w-3 h-3 inline" /> limpiar
                        </button>
                      </div>
                    }
                  >
                    {recents.map((r) => {
                      const Icon = ICONS[r.icon] || Clock;
                      return (
                        <CommandItem
                          key={`recent-${r.type}-${r.id}`}
                          value={`recent-${r.type}-${r.id}-${r.label}`}
                          onSelect={() => handleSelect(r)}
                          data-testid={`recent-${r.type}-${r.id}`}
                          className="flex items-start gap-2 cursor-pointer"
                        >
                          <Clock className="w-3 h-3 mt-1 flex-shrink-0 text-slate-400" />
                          <Icon className="w-4 h-4 mt-0.5 flex-shrink-0 text-slate-500" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{r.label}</div>
                            <div className="text-[11px] text-muted-foreground truncate">{r.sublabel}</div>
                          </div>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                  <CommandSeparator />
                </>
              )}

              {/* Fallback removed — when no recents, show only command groups */}

              {/* Comandos de acción (siempre visibles, también filtrados por query) */}
              {filteredCommands.length > 0 && (
                <>
                  {commandsByKind.action.length > 0 && (
                    <CommandGroup heading="Acciones rápidas">
                      {commandsByKind.action.map((cmd) => {
                        const Icon = ICONS[cmd.icon] || Zap;
                        return (
                          <CommandItem
                            key={cmd.id}
                            value={`cmd-${cmd.id}`}
                            onSelect={() => executeAction(cmd)}
                            data-testid={`cmd-${cmd.id}`}
                            className="flex items-start gap-2 cursor-pointer"
                          >
                            <Icon className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium truncate">{cmd.label}</div>
                              <div className="text-[11px] text-muted-foreground truncate">{cmd.sublabel}</div>
                            </div>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  )}
                  {commandsByKind.create.length > 0 && (
                    <>
                      {commandsByKind.action.length > 0 && <CommandSeparator />}
                      <CommandGroup heading="Crear">
                        {commandsByKind.create.map((cmd) => {
                          const Icon = ICONS[cmd.icon] || Plus;
                          return (
                            <CommandItem
                              key={cmd.id}
                              value={`cmd-${cmd.id}`}
                              onSelect={() => executeAction(cmd)}
                              data-testid={`cmd-${cmd.id}`}
                              className="flex items-start gap-2 cursor-pointer"
                            >
                              <Icon className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-600" />
                              <div className="flex-1 min-w-0">
                                <div className="font-medium truncate">{cmd.label}</div>
                                <div className="text-[11px] text-muted-foreground truncate">{cmd.sublabel}</div>
                              </div>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </>
                  )}
                  {commandsByKind.nav.length > 0 && (
                    <>
                      {(commandsByKind.action.length + commandsByKind.create.length) > 0 && <CommandSeparator />}
                      <CommandGroup heading="Ir a...">
                        {commandsByKind.nav.map((cmd) => {
                          const Icon = ICONS[cmd.icon] || LayoutDashboard;
                          return (
                            <CommandItem
                              key={cmd.id}
                              value={`cmd-${cmd.id}`}
                              onSelect={() => executeAction(cmd)}
                              data-testid={`cmd-${cmd.id}`}
                              className="flex items-start gap-2 cursor-pointer"
                            >
                              <Icon className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-600" />
                              <div className="flex-1 min-w-0">
                                <div className="font-medium truncate">{cmd.label}</div>
                                <div className="text-[11px] text-muted-foreground truncate">{cmd.sublabel}</div>
                              </div>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </>
                  )}
                </>
              )}

              {/* Resultados del backend */}
              {!loading && results.length > 0 && (
                <>
                  <CommandSeparator />
                  {TYPE_ORDER.filter((t) => grouped[t]?.length).map((type, idx) => (
                    <div key={type}>
                      {idx > 0 && <CommandSeparator />}
                      <CommandGroup heading={`${TYPE_LABELS[type]} (${grouped[type].length})`}>
                        {grouped[type].map((r, i) => {
                          const Icon = ICONS[r.icon] || FileText;
                          return (
                            <CommandItem
                              key={`${r.type}-${i}-${r.label}`}
                              value={`${r.type}-${i}-${r.label}`}
                              onSelect={() => handleSelect(r)}
                              data-testid={`search-result-${r.type}-${i}`}
                              className="flex items-start gap-2 cursor-pointer"
                            >
                              <Icon className="w-4 h-4 mt-0.5 flex-shrink-0 text-slate-500" />
                              <div className="flex-1 min-w-0">
                                <div className="font-medium truncate">{r.label}</div>
                                <div className="text-[11px] text-muted-foreground truncate">{r.sublabel}</div>
                              </div>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </div>
                  ))}
                </>
              )}

              {!loading && q.trim() && results.length === 0 && filteredCommands.length === 0 && filteredContextCommands.length === 0 && (
                <CommandEmpty>Sin resultados para "{q}"</CommandEmpty>
              )}

              {!q.trim() && (
                <div className="py-3 px-4 border-t text-center text-[11px] text-muted-foreground">
                  Atajos: <kbd className="border rounded px-1.5 py-0.5 mx-0.5">↑↓</kbd> navegar · <kbd className="border rounded px-1.5 py-0.5 mx-0.5">↵</kbd> ejecutar · <kbd className="border rounded px-1.5 py-0.5 mx-0.5">Esc</kbd> cerrar
                </div>
              )}
            </CommandList>
          </CommandPrimitive>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default GlobalSearch;
